import PropTypes from 'prop-types';
import React, {useState, useEffect, useMemo} from 'react';
import {Helmet} from 'react-helmet';
import {TabContext, TabList, TabPanel} from '@material-ui/lab';
import {Box, Tab, Tooltip} from '@material-ui/core';
import {grey} from '@material-ui/core/colors';
import {Attachment, Visibility} from '@material-ui/icons';
import ImportedProjectToolbar from 'ImportedProjectSuite/components/ImportedProjectToolbar';
import {expectedOrderOfCnsProceduresTemplate} from 'ImportedProjectLobby/utils/ImportedProjectUtils';
import {
  findImportedProjectById,
  findimportedProjectMilestones,
  findProjectDocuments,
} from 'Projects/service/ProjectService';
import MilestoneList from 'ImportedProjectSuite/components/MilestoneList';
import DocumentList from 'ImportedProjectSuite/components/DocumentList';
import Loader from 'util/Loader';

// this is the hard coded template of sorted procedures for milestone-2 [construct: targeting vector]

const PROJECT_DELIMITER = '_';
const extractProjectNumber = (name) => {
  const trimmed = name.trim();
  const match = trimmed.match(/^(?:P-)?(\d+)[_ ]/i);

  return match ? match[1] : null;
};

export default function ImportedProjectSuiteView(props) {
  const {
    params: {importedProjectId},
  } = props;
  const [stepIndex, setStepIndex] = useState(0);
  const [importedProject, setImportedProject] = useState(null);
  const [projectMilestones, setProjectMilestones] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [completedEstimate, setCompletedEstimate] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('0');

  useEffect(() => {
    const handleRetrieveProject = async () => {
      const project = await findImportedProjectById(importedProjectId);
      const projectNumber = extractProjectNumber(project.cnsObject.name);

      setImportedProject(project);
      if (projectNumber) {
        const documents = await findProjectDocuments(projectNumber);

        setDocuments(documents);
      }
    };

    if (isLoading && !!importedProjectId) {
      handleRetrieveProject();
    }
  }, [importedProjectId, isLoading]);

  useEffect(() => {
    const handleRetrieveMilestones = async () => {
      const {
        cnsObject: {createdDt},
      } = importedProject;
      const projectMilestones = await findimportedProjectMilestones(importedProjectId);
      const {cnsProcedures: cnsProceduresToSort} = projectMilestones[1];

      cnsProceduresToSort.sort(
        (a, b) =>
          expectedOrderOfCnsProceduresTemplate.indexOf(a.name) <
          expectedOrderOfCnsProceduresTemplate.indexOf(b.name),
      );

      let completedEstimate = new Date(createdDt).getTime();

      for (const milestoneWrapper of projectMilestones) {
        const {todos, cnsProcedures, milestone} = milestoneWrapper;

        if (todos.length > 0) {
          if (cnsProcedures.length === 0 && todos.every(({finished}) => finished)) {
            // completed - use latest finished todo
            completedEstimate = Math.max(...todos.map((todo) => new Date(todo.finished).getTime()));
          } else {
            // in progress - use latter of milestone duration or now + remaining durations
            // only count each procedure once to avoid stream duplication
            const includedProcedureIds = new Set();
            // started and not finished and not already in the list of procedure ids
            const startedTodoCompletionDate = Math.max(
              0,
              ...todos
                .filter(
                  ({finished, started, cnsProcedure: {id}}) =>
                    started &&
                    !finished &&
                    includedProcedureIds.size < includedProcedureIds.add(id).size,
                )
                .map(
                  ({started, cnsProcedure: {duration}}) => new Date(started).getTime() + duration,
                ),
            );

            completedEstimate = Math.max(
              // start from start date + duration if its after today, otherwise 2 days from now
              (startedTodoCompletionDate > new Date().getTime()
                ? startedTodoCompletionDate
                : new Date().getTime() + 2 * 24 * 60 * 60 * 1000) +
                todos
                  .filter(
                    ({started, finished, cnsProcedure: {id}}) =>
                      // not started and not finished and not already in the list of procedure ids
                      !started &&
                      !finished &&
                      includedProcedureIds.size < includedProcedureIds.add(id).size,
                  )
                  .reduce((sum, {cnsProcedure: {duration}}) => sum + duration, 0) +
                cnsProcedures.reduce((sum, {duration}) => sum + duration, 0),
              completedEstimate + milestone.duration,
            );
          }
        } else {
          // future - use milestone duration from latter of now / previous milestone completion
          completedEstimate =
            Math.max(new Date().getTime(), completedEstimate) + milestone.duration;
        }
        milestoneWrapper.completedEstimate = completedEstimate;
      }

      setProjectMilestones(projectMilestones);
      setCompletedEstimate(completedEstimate);
      setIsLoading(false);
    };

    if (importedProject !== null) {
      handleRetrieveMilestones();
    }
  }, [importedProject, importedProjectId]);

  const handleChangeStepIndex = (stepIndex) => setStepIndex(stepIndex);
  const timelineEnabled = useMemo(
    () =>
      projectMilestones.filter(
        (projectMilestone) =>
          projectMilestone.todos.length !== 0 || projectMilestone.cnsProcedures.length !== 0,
      ),
    [projectMilestones],
  );
  const handleChangeTabs = (_event, newValue) => setSelectedTab(newValue);

  return isLoading ? (
    <Loader label="Loading Project Data" />
  ) : (
    <div>
      <Helmet title={importedProject.cnsObject.name} defer={false} />

      <ImportedProjectToolbar
        importedProject={importedProject}
        projectMilestones={projectMilestones}
        completedEstimate={completedEstimate}
      />

      <TabContext value={selectedTab}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: grey[200],
            borderRadius: '16px',
            marginLeft: 10,
            marginRight: 10,
            marginTop: 10,
          }}
        >
          <TabList variant="fullWidth" onChange={handleChangeTabs}>
            <Tab
              style={{
                backgroundColor: selectedTab === '0' && 'lightgray',
                borderTopLeftRadius: '16px',
                borderBottomLeftRadius: '16px',
              }}
              disabled={timelineEnabled.length === 0}
              icon={<Visibility />}
              label={
                <Tooltip
                  title={timelineEnabled.length === 0 ? 'No Timeline Data' : 'View Timeline'}
                >
                  <span>Timeline</span>
                </Tooltip>
              }
              value="0"
            />
            <Tab
              style={{
                backgroundColor: selectedTab === '1' && 'lightgray',
                borderTopRightRadius: '16px',
                borderBottomRightRadius: '16px',
              }}
              disabled={documents.length === 0}
              icon={<Attachment />}
              label={
                <Tooltip title={documents.length === 0 ? 'No Documents' : 'View Documents'}>
                  <span>Documents</span>
                </Tooltip>
              }
              value="1"
            />
          </TabList>
        </Box>
        {timelineEnabled.length > 0 && (
          <TabPanel value="0">
            <MilestoneList
              stepIndex={stepIndex}
              projectMilestones={projectMilestones}
              onChangeStepIndex={handleChangeStepIndex}
            />
          </TabPanel>
        )}
        {documents.length > 0 && (
          <TabPanel value="1">
            <DocumentList documents={documents} />
          </TabPanel>
        )}
      </TabContext>
    </div>
  );
}

ImportedProjectSuiteView.propTypes = {
  params: PropTypes.object.isRequired,
};
